// pricing-calculator/utils/roi.ts
// ROI computations (pure, UI-agnostic)

import type { Range, ROIInputs, ROIOutputs } from '@dopomogai/types';
import { ZERO_RANGE, addRange, clampRange, isFiniteNumber } from './range';

/**
 * Compute added orders purely from sessions and absolute pp lift.
 * Note: This does not need base CVR; pp lift is absolute.
 */
export function computeAddedOrdersRange(sessions?: number, convLiftPp?: Range): Range | undefined {
  if (!isFiniteNumber(sessions) || !convLiftPp) return undefined;
  const liftMin = convLiftPp[0] / 100;
  const liftMax = convLiftPp[1] / 100;
  const min = Math.max(0, Math.floor(sessions! * liftMin));
  const max = Math.max(0, Math.ceil(sessions! * liftMax));
  return [min, max];
}

/**
 * Compute added revenue = added orders * AOV
 */
export function computeAddedRevenueRange(addedOrders?: Range, aov?: number): Range | undefined {
  if (!addedOrders || !isFiniteNumber(aov)) return undefined;
  const [oMin, oMax] = addedOrders;
  return [oMin * aov!, oMax * aov!];
}

/**
 * Compute gross profit impact = revenue * margin%
 */
export function computeGrossProfitImpactRange(revenue?: Range, marginPct?: number): Range | undefined {
  if (!revenue || !isFiniteNumber(marginPct)) return undefined;
  const m = marginPct! / 100;
  return [revenue[0] * m, revenue[1] * m];
}

/**
 * Compute support savings from tickets reduced * cost per ticket
 */
export function computeSupportSavingsRange(
  ticketsMonthly?: number,
  costPerTicket?: number,
  supportReductionPct?: Range
): Range | undefined {
  if (!isFiniteNumber(ticketsMonthly) || !isFiniteNumber(costPerTicket) || !supportReductionPct) return undefined;
  const redMin = supportReductionPct[0] / 100;
  const redMax = supportReductionPct[1] / 100;
  const savedTickets: Range = [ticketsMonthly! * redMin, ticketsMonthly! * redMax];
  return [savedTickets[0] * costPerTicket!, savedTickets[1] * costPerTicket!];
}

/**
 * Full ROI computation with conservative ranges.
 * For parity with legacy UI behavior, revenue/grossProfit requires sessions + cvrPct + aov + marginPct.
 * Note: mathematically, added orders depend only on sessions and absolute pp lift; CVR is not needed.
 */
export function computeROI(inputs: ROIInputs, convLiftPp: Range, supportReductionPct: Range): ROIOutputs {
  const { sessions, cvrPct, aov, marginPct, ticketsMonthly, costPerTicket } = inputs;
  const outputs: ROIOutputs = {};

  // Revenue-side: keep legacy gating (requires cvrPct and marginPct)
  const haveRevenueSide =
    isFiniteNumber(sessions) && isFiniteNumber(cvrPct) && isFiniteNumber(aov) && isFiniteNumber(marginPct);

  if (haveRevenueSide) {
    const addedOrders = computeAddedOrdersRange(sessions, convLiftPp);
    if (addedOrders) {
      outputs.addedOrdersRange = addedOrders;
      const addedRevenue = computeAddedRevenueRange(addedOrders, aov);
      if (addedRevenue) {
        outputs.addedRevenueRange = addedRevenue;
        const gross = computeGrossProfitImpactRange(addedRevenue, marginPct);
        if (gross) outputs.grossProfitImpactRange = gross;
      }
    }
  }

  // Support-side
  const haveSupportSide = isFiniteNumber(ticketsMonthly) && isFiniteNumber(costPerTicket);
  if (haveSupportSide) {
    const supportSavings = computeSupportSavingsRange(ticketsMonthly, costPerTicket, supportReductionPct);
    if (supportSavings) outputs.supportSavingsRange = supportSavings;
  }

  // Combined
  if (outputs.grossProfitImpactRange || outputs.supportSavingsRange) {
    const a = outputs.grossProfitImpactRange || ZERO_RANGE;
    const b = outputs.supportSavingsRange || ZERO_RANGE;
    outputs.totalMonthlyImpactRange = addRange(a, b);
  }

  return outputs;
}

// Return true if any ROI input is provided as a finite number
export function hasAnyROIInput(inputs?: Partial<ROIInputs> | ROIInputs): boolean {
  if (!inputs) return false;
  const { sessions, cvrPct, aov, marginPct, ticketsMonthly, costPerTicket } = inputs;
  return [sessions, cvrPct, aov, marginPct, ticketsMonthly, costPerTicket].some(isFiniteNumber);
}
